import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, AlertTriangle } from 'lucide-react';

/**
 * Compact subscription/plan summary panel for the Settings tab.
 * Props:
 *  - plan: active plan object (nullable)
 *  - loading: boolean loading state
 *  - onManage: callback to open billing tab
 */
const OwnerAccountPanel = ({ plan, loading, onManage }) => {
  const { t } = useTranslation();

  const planMeta = useMemo(() => {
    if (!plan) return { status: 'none', label: t('subscription.noActivePlan', 'No active plan'), daysLeft: null, expired: false };    
    let daysLeft = null;
    let expired = false;
    if (plan.expiresAt) {
      try {
        const expiry = new Date(plan.expiresAt);
        const diff = Math.ceil((expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        daysLeft = diff;
        if (diff < 0) expired = true;
      } catch {}
    }
    return {
      status: expired ? 'expired' : 'active',
      label: plan.planName || plan.planCode || t('subscription.activePlan', 'Active Plan'),
      daysLeft,
      expired
    };
  }, [plan, t]);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center">
          <CreditCard className="w-5 h-5 mr-2" />
          {t('subscription.accountPlan', 'Subscription')}
        </h3>
        {planMeta.status === 'expired' && (
          <span className="flex items-center text-red-400 text-xs font-medium">
            <AlertTriangle className="w-4 h-4 mr-1" /> {t('subscription.expired.badge', 'Expired')}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center space-x-2 text-amber-300">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500" />
          <span className="text-sm">{t('subscription.loadingPlan', 'Loading plan...')}</span>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <p className="text-sm text-gray-400 mb-1">{t('subscription.current', 'Current Plan')}</p>
            <p className="text-white font-medium">{planMeta.label}</p>
          </div>
          {planMeta.daysLeft !== null && (
            <div>
              <p className="text-sm text-gray-400 mb-1">{t('subscription.daysLeft', 'Days Left')}</p>
              <p className={`font-medium ${planMeta.expired ? 'text-red-400' : 'text-amber-300'}`}>{planMeta.expired ? t('subscription.expired.now', 'Expired') : planMeta.daysLeft}</p>
            </div>
          )}
          {plan?.billingInterval && (
            <div>
              <p className="text-sm text-gray-400 mb-1">{t('subscription.interval', 'Billing Interval')}</p>
              <p className="text-white font-medium lowercase">{plan.billingInterval.toLowerCase()}</p>
            </div>
          )}
          {plan?.activatedAt && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400 mb-0.5">{t('subscription.activatedOn', 'Activated')}</p>
                <p className="text-gray-200">{new Date(plan.activatedAt).toLocaleDateString()}</p>
              </div>
              {plan?.expiresAt && (
                <div>
                  <p className="text-gray-400 mb-0.5">{t('subscription.expiresOn', 'Expires')}</p>
                  <p className="text-gray-200">{new Date(plan.expiresAt).toLocaleDateString()}</p>
                </div>
              )}
            </div>
          )}
          <div className="pt-2">
            <button
              onClick={() => onManage ? onManage() : window.dispatchEvent(new CustomEvent('dashboard-switch-tab', { detail: { tab: 'billing' } }))}
              className="btn-primary w-full"
            >
              {planMeta.status === 'expired' ? t('subscription.renewNow', 'Renew Now') : t('subscription.managePlan', 'Manage Plan')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerAccountPanel;
